'use client';

import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'almanak-recent-searches';
const MAX_ITEMS = 5;

interface RecentItem {
  query: string;
  href: string;
  title: string;
}

function getStorageKey(lang: string): string {
  return lang && lang !== 'en' ? `${STORAGE_KEY_PREFIX}-${lang}` : STORAGE_KEY_PREFIX;
}

function readFromStorage(lang: string): RecentItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getStorageKey(lang));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeToStorage(items: RecentItem[], lang: string) {
  try {
    localStorage.setItem(getStorageKey(lang), JSON.stringify(items));
  } catch {
    // localStorage full or disabled
  }
}

export function useRecentSearches(lang: string = 'en') {
  const [items, setItems] = useState<RecentItem[]>(() => readFromStorage(lang));

  // Re-read from storage when language changes
  useEffect(() => {
    setItems(readFromStorage(lang));
  }, [lang]);

  const add = useCallback((item: RecentItem) => {
    setItems((prev) => {
      const filtered = prev.filter((r) => r.href !== item.href);
      const next = [item, ...filtered].slice(0, MAX_ITEMS);
      writeToStorage(next, lang);
      return next;
    });
  }, [lang]);

  const remove = useCallback((href: string) => {
    setItems((prev) => {
      const next = prev.filter((r) => r.href !== href);
      writeToStorage(next, lang);
      return next;
    });
  }, [lang]);

  const clear = useCallback(() => {
    setItems([]);
    writeToStorage([], lang);
  }, [lang]);

  return { items, add, remove, clear };
}
